class CollectionScrollPosition {
  constructor () {
    // Last clicked item (product) href
    this.lastClickedItemHrefKey =
      'collectionScrollPosition-lastClickedItemHref'

    this.lastClickedItemHref = sessionStorage.getItem(
      this.lastClickedItemHrefKey,
    )

    // Collection URL where the last click happened
    this.lastClickedCollectionUrlKey =
      'collectionScrollPosition-lastClickedCollectionUrl'

    this.lastClickedCollectionUrl = sessionStorage.getItem(
      this.lastClickedCollectionUrlKey,
    )

    // Last visited collection page (pagination)
    this.lastVisitedUrlsPageKey =
      'collectionScrollPosition-lastVisitedCollectionUrlPage'

    this.lastVisitedUrlsPage = sessionStorage.getItem(
      this.lastVisitedUrlsPageKey,
    )

    // Current URL (pathname only, no query)
    this.currentUrl = this.urlToPathName(window.location.href)
  }

  initialise () {
    const isCollection = this.currentUrl.includes('/collections/')
    const isProduct = this.currentUrl.includes('/products/')

    if (isProduct) return

    if (isCollection) {
      this.handleCollectionTemplate()
    } else {
      this.clearData()
    }
  }

  handleCollectionTemplate () {
    // Disable browser's native scrollRestoration
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }

    // If we have a stored page, ensure the URL reflects it (e.g. ?page=2)
    if (this.lastVisitedUrlsPage && this.lastVisitedUrlsPage !== '1') {
      const url = new URL(window.location.href)

      if (!url.searchParams.get('page')) {
        url.searchParams.set('page', this.lastVisitedUrlsPage)
        history.replaceState(null, '', url.toString())
      }
    }

    this.restoreScrollPosition()
    this.captureScrollPosition()
  }

  restoreScrollPosition () {
    // Only restore if:
    // 1. We have a last clicked product
    // 2. We have a collection URL associated with that click
    // 3. The current collection matches that stored collection
    if (
      !this.lastClickedItemHref ||
      this.lastClickedItemHref === 'undefined' ||
      !this.lastClickedCollectionUrl ||
      this.lastClickedCollectionUrl !== this.currentUrl
    ) {
      return
    }

    const maxTries = 20

    const scrollToElement = () => {
      const el = this.getLastClickedItem()

      if (el) {
        requestAnimationFrame(() => {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          })

          // Keep keyboard navigation positioned at the restored product,
          // but suppress the focus styling until the user uses the keyboard.
          el.setAttribute('data-scroll-restored-focus', '')
          el.focus({ preventScroll: true })

          const removeRestoredFocus = () => {
            el.removeAttribute('data-scroll-restored-focus')
          }

          window.addEventListener('keydown', removeRestoredFocus, {
            once: true,
          })

          el.addEventListener('blur', removeRestoredFocus, {
            once: true,
          })

          // We're done, clear state
          this.clearData()
        })
      } else {
        if (!this._scrollTries) this._scrollTries = 0

        if (this._scrollTries < maxTries) {
          this._scrollTries++
          setTimeout(scrollToElement, 50)
        } else {
          // Stop trying after maxTries
          this._scrollTries = 0
        }
      }
    }

    this._scrollTries = 0
    scrollToElement()
  }

  getLastClickedItem () {
    const normalisedHref = this.normaliseProductUrl(this.lastClickedItemHref)

    if (!normalisedHref) return null

    const anchors = document.querySelectorAll('a[href*="/products/"]')

    return (
      [...anchors].find((anchor) => {
        return (
          this.normaliseProductUrl(anchor.getAttribute('href')) ===
          normalisedHref
        )
      }) || null
    )
  }

  captureScrollPosition () {
    // Capture clicked product on collection pages
    window.addEventListener('click', (e) => {
      const anchor = e.target.closest('a[href*="/products/"]')
      const item = e.target.closest('[data-page]')

      if (!anchor || !item) return

      const href = anchor.getAttribute('href')
      const normalisedHref = this.normaliseProductUrl(href)

      if (!normalisedHref) return

      e.preventDefault()

      const page = item.dataset.page

      // Store the normalised product URL
      sessionStorage.setItem(this.lastClickedItemHrefKey, normalisedHref)
      this.lastClickedItemHref = normalisedHref

      // Store the collection URL we are currently on (pathname only)
      const collectionPath = window.location.pathname

      sessionStorage.setItem(this.lastClickedCollectionUrlKey, collectionPath)
      this.lastClickedCollectionUrl = collectionPath

      // Store the page we were on when clicking
      if (page != null) {
        sessionStorage.setItem(this.lastVisitedUrlsPageKey, page)
        this.lastVisitedUrlsPage = page

        const currentParams = new URLSearchParams(window.location.search)

        currentParams.set('page', page)

        const newUrl = `${window.location.pathname}?${currentParams.toString()}`

        history.pushState({ page }, '', newUrl)
      }

      window.location.href = href
    })

    // Capture beforeunload event
    window.addEventListener('beforeunload', () => {
      sessionStorage.setItem(
        this.lastClickedItemHrefKey,
        this.lastClickedItemHref || '',
      )

      sessionStorage.setItem(
        this.lastClickedCollectionUrlKey,
        this.lastClickedCollectionUrl || '',
      )
    })
  }

  normaliseProductUrl (url) {
    if (!url) return null

    try {
      const object = new URL(url, window.location.origin)
      const match = object.pathname.match(/\/products\/([^/]+)/)

      if (!match) return null

      return `/products/${match[1]}`
    } catch {
      return null
    }
  }

  clearData () {
    sessionStorage.removeItem(this.lastClickedItemHrefKey)
    sessionStorage.removeItem(this.lastClickedCollectionUrlKey)
    sessionStorage.removeItem(this.lastVisitedUrlsPageKey)

    this.lastClickedItemHref = null
    this.lastClickedCollectionUrl = null
    this.lastVisitedUrlsPage = null
  }

  urlToPathName (url) {
    const object = new URL(url)

    return object.pathname
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const collectionScrollPosition = new CollectionScrollPosition()

  collectionScrollPosition.initialise()
})
